import { useState, useEffect, useContext } from "react";
import { Tabs, Tab, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import ActiveTaskTab from "./components/ActiveTaskTab";
import TaskDialog from "./components/TaskDialog";
import ArchivedTasksTab from "./components/ArchivedTaskTab";
import { ApiContext } from "../../App";

const Tasks = ({  facility }) => {
	const { t } = useTranslation();

	const [activeTab, setActiveTab] = useState("tasks");
	const [tasks, setTasks] = useState([]);

	const defaultDialogData = {
		facility: facility,
		linked_alert: "",
		status: "OPEN",
		description: "",
		location: "",
		system: "",
		device: "",
		details: "",
		comments: "",
	};

	const [dialogData, setDialogData] = useState(defaultDialogData);
	const [dialogIsOpen, setDialogIsOpen] = useState(false);
	const [alerts, setAlerts] = useState([]);
	const [dialogType, setDialogType] = useState("add");

	const {tasksApi, alertsApi} = useContext(ApiContext)

	useEffect(() => {
		tasksApi.list(
			facility,
			(data) => {
				setTasks(data);
			},
			() => {
				alert(t("Error fetching tasks"));
			}
		);
	}, [ facility]);

	useEffect(() => {
		alertsApi.list(
			facility,
			(data) => {
				setAlerts(data);
			},
			() => {
				alert(t("Error fetching alerts"));
			}
		);
	}, [ facility]);

	const tabChangeHandler = (event, newValue) => {
		setActiveTab(newValue);
	};

	const addTask = () => {
		tasksApi.create(
			dialogData,
			(data) => {
				setDialogIsOpen(!dialogIsOpen);
				setTasks([...tasks, { ...dialogData, url: data.url }]);
				resetDialogData();
			},
			() => {
				alert(t("Failed to create task."));
			}
		);
	};

	const editTask = () => {
		tasksApi.update(
			dialogData.url,
			dialogData,
			() => {
				setTasks(
					tasks.map((task) => {
						if (task.url === dialogData.url) {
							return dialogData;
						}
						return task;
					})
				);
				toggleDialogIsOpen();
				resetDialogData();
			},
			() => {
				alert(t("Failed to update task."));
			}
		);
	};

	const resetDialogData = () => {
		setDialogData(defaultDialogData);
	};

	const handleTask = () => {
		if (dialogType === "add") {
			addTask();
		} else if (dialogType === "edit") {
			editTask();
		}
	};

	const copyTask = (taskUrl) => {
		const task = tasks.find((task) => task.url === taskUrl);
		const newTask = { ...task };
		delete newTask.url;
		tasksApi.create(
			newTask,
			(data) => {
				setTasks([...tasks, { ...newTask, url: data.url }]);
			},
			() => {
				alert(t("Failed to copy task"));
			}
		);
	};

	const deleteTask = (url) => {
		const confirmed = window.confirm(t("Delete this Task?"));
		if (confirmed) {
			tasksApi.delete(
				url,
				(data) => {
					setTasks(tasks.filter((task) => task.url !== url));
				},
				() => {
					alert(t("Failed to delete task."));
				}
			);
		}
	};

	const toggleDialogIsOpen = () => {
		setDialogIsOpen(!dialogIsOpen);
	};

	return (
		<Box >
			<Tabs
				value={activeTab}
				onChange={tabChangeHandler}
			>
				<Tab
					value="tasks"
					label={t("Tasks")}
				/>
				<Tab
					value="archived"
					label={t("Archived")}
				/>
			</Tabs>
			<Box
				sx={{
					padding: "24px",
					backgroundColor: (theme) =>
						theme.palette.mode === "light"
							? theme.palette.grey[100]
							: theme.palette.grey[900],
					overflow: "auto",
				}}
			>
				{activeTab === "tasks" && (
					<>
						<ActiveTaskTab
							tasks={tasks}
							t={t}
							dialogIsOpen={dialogIsOpen}
							setDialogIsOpen={setDialogIsOpen}
							editTask={editTask}
							toggleDialogIsOpen={toggleDialogIsOpen}
							setDialogType={setDialogType}
							setDialogData={setDialogData}
							deleteTask={deleteTask}
							resetDialogData={resetDialogData}
							copyTask={copyTask}
						/>
					</>
				)}
				{activeTab === "archived" && (
					<ArchivedTasksTab
						tasks={tasks.filter((task) => task.status === "CLOSED")}
						t={t}
						editTask={editTask}
						toggleDialogIsOpen={toggleDialogIsOpen}
						setDialogType={setDialogType}
						setDialogData={setDialogData}
						deleteTask={deleteTask}
						resetDialogData={resetDialogData}
						copyTask={copyTask}
					/>
				)}
				<TaskDialog
					alerts={alerts}
					dialogData={dialogData}
					setDialogData={setDialogData}
					toggleDialogIsOpen={toggleDialogIsOpen}
					handleTask={handleTask}
					dialogIsOpen={dialogIsOpen}
					t={t}
				/>
			</Box>
		</Box>
	);
};

export default Tasks;
